import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "./Button";
import { Input } from "./inputs/Input";
import {
  X,
  Mail,
  Building2,
  MessageSquare,
  User,
  Shield,
  Send,
  Clock3,
} from "lucide-react";
import RenderLogo from "./RenderLogo";
import "./MockSignupFlow.scss";

const SHEET_APP_URL = "https://script.google.com/macros/s/AKfycbxWQsk8MdMkFm0tFdPIIqhHHOFlr6iashZV2XvpOCs4NlkjfesLPvbQ_yC9VQqj3ZXY/exec";

const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email);

export default function ContactUsFlow({
  isOpen,
  onClose,
  initialEmail = "",
  plan,
  onSuccess,
}) {
  const [formData, setFormData] = useState({
    fullName: "",
    email: initialEmail,
    company: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (initialEmail && initialEmail !== formData.email) {
      setFormData((prev) => ({
        ...prev,
        email: initialEmail,
      }));
    }
  }, [initialEmail, formData.email]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  const planName = plan?.name || "Custom";
  const planPriceLabel = useMemo(() => {
    if (!plan?.price) return "";
    return plan?.period ? `${plan.price}/${plan.period}` : plan.price;
  }, [plan]);

  const updateField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const sendToGoogleSheet = async () => {
    try {
      await fetch(SHEET_APP_URL, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          type: "contact_us",
          timestamp: new Date().toISOString(),
          plan_name: planName,
          plan_price: planPriceLabel || "N/A",
          full_name: formData.fullName.trim(),
          email: formData.email.trim(),
          company: formData.company.trim(),
          message: formData.message.trim(),
        }),
      });
      // console.log("Successfully sent contact request to Google Sheet");
    } catch (error) {
      // console.error("Error sending contact request to Google Sheet:", error);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      window.rdt?.("track", "Lead");
      await sendToGoogleSheet();
      onSuccess?.(`Thanks, ${formData.fullName}. We will contact you at ${formData.email}.`);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.96, opacity: 0, y: 16 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.96, opacity: 0, y: 16 }}
          className="mx-4 w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="border-b border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50 px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <RenderLogo showText={false} size={34} />
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Contact Sales</h2>
                  <p className="text-sm text-slate-600">Tell us what you need and we will follow up by email</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>

          <div className="grid gap-6 p-6 ">
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Full name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Jane Doe"
                    value={formData.fullName}
                    onChange={(event) => updateField("fullName", event.target.value)}
                    className={`pl-20 ${errors.fullName ? "border-red-300 focus:border-red-500" : ""}`}
                  />
                </div>
                {errors.fullName && <p className="text-sm text-red-600">{errors.fullName}</p>}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Email <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    type="email"
                    placeholder="jane@company.com"
                    value={formData.email}
                    onChange={(event) => updateField("email", event.target.value)}
                    className={`pl-10 ${errors.email ? "border-red-300 focus:border-red-500" : ""}`}
                  />
                </div>
                {errors.email && <p className="text-sm text-red-600">{errors.email}</p>}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Company</label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Acme Inc."
                    value={formData.company}
                    onChange={(event) => updateField("company", event.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  What are you looking for? <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <textarea
                    rows={5}
                    placeholder="Share your use case, expected usage, and any requirements."
                    value={formData.message}
                    onChange={(event) => updateField("message", event.target.value)}
                    className={`w-full rounded-xl border bg-white py-2 pl-10 pr-3 text-sm placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900/10 ${
                      errors.message ? "border-red-300 focus:border-red-500" : "border-slate-300"
                    }`}
                  />
                </div>
                {errors.message && <p className="text-sm text-red-600">{errors.message}</p>}
              </div>
            </div>

            {/* <div className="space-y-4">
              <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
                <div className="mb-3 flex items-center gap-2 text-blue-700">
                  <Send className="h-4 w-4" />
                  <span className="text-sm font-semibold">Selected Plan</span>
                </div>
                <div className="rounded-xl bg-white p-3">
                  <p className="text-sm font-semibold text-slate-900">{planName}</p>
                  {planPriceLabel && <p className="text-sm text-slate-600">{planPriceLabel}</p>}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm">
                <div className="mb-2 flex items-center gap-2 font-semibold text-slate-900">
                  <Clock3 className="h-4 w-4 text-slate-600" />
                  <span>What happens next</span>
                </div>
                {/* <p className="text-slate-600">We log your request directly to our contact queue.</p>
                <p className="mt-2 text-slate-600">Our team replies with a tailored plan and pricing details.</p>
              </div>
            </div> */}
          </div>

          <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-6 py-4">
            <div className="flex items-center gap-2 text-xs text-slate-600">
              {/* <Shield className="h-4 w-4" /> */}
              {/* <span>Business inquiry only. No payment is requested in this step.</span> */}
            </div>
            <Button onClick={handleSubmit} disabled={isSubmitting} className="gradient-primary hover:gradient-primary-hover">
              {isSubmitting ? "Sending..." : "Send"}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
